// eslint-disable-next-line no-restricted-imports
import type { Theme } from '@emotion/react';
// eslint-disable-next-line no-restricted-imports
import { useTheme } from '@emotion/react';
// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import { useStoreSpecials } from '@iheartjane/data-access/react-query';
import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  doesSpecialApply,
  getMenuPrices,
  getSpecialIdsForTagLabel,
  getSpecialTagLabel,
  hideStrikethroughPrice,
  isPriceType,
} from '@iheartjane/menu';
import {
  AddIcon,
  Box,
  Card,
  CheckIcon,
  ConditionalWrapper,
  Flex,
  Typography,
} from '@iheartjane/reefer';
import type { PriceId, Weight } from '@iheartjane/types';
import { useQueryClient } from '@tanstack/react-query';
import delay from 'lodash-es/delay';
import get from 'lodash-es/get';
import { transparentize } from 'polished';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { useLocation, useMatches, useNavigate, useParams } from 'react-router';

import type {
  AlgoliaPriceId,
  AlgoliaProduct,
  JaneSearchState,
} from '@jane/search/types';
import { useGetStoreSpecials } from '@jane/shared-ecomm/data-access';
import { useGetStore } from '@jane/shared/data-access';
import type { Store, StoreSpecial } from '@jane/shared/types';
import {
  calculateDiscount,
  calculateDiscountPercentage,
  getApplicableGoldTagLabel,
  getProductImages,
} from '@jane/shared/util';

import type { RootLoaderType } from '../../../data-access';
import { useKioskUserSegmentIds } from '../../../data-access';
import {
  useGetProductPrices,
  useKioskSpacing,
  useShouldKioskShowGold,
  useTypedRouteLoaderData,
} from '../../../hooks';
import { useBrandSpecials } from '../../../providers';
import type { AddToBagParams, MenuCategory } from '../../../types';
import {
  buildPriceString,
  customCategoryLabel,
  customLineageLabel,
  customSubcategoryLabel,
  getCategoryTagColor,
  getPriceIdForPercentThc,
  getSource,
  potencyDescription,
  trackProductCardTap,
} from '../../../util';
import { DealTag } from '../DealTag/DealTag';
import { IconButton } from '../IconButton/IconButton';
import { StarRating } from '../StarRating/StarRating';
import { getProductStatusLabel } from './ProductCard.util';
import { ProductCardPrice } from './ProductCardPrice';
import { useProductDetailsPath } from './useProductDetailsPath';

const CardHeaderBox = styled(Box)({
  aspectRatio: '10/9',
  pointerEvents: 'none',
  position: 'absolute' as const,
  width: '100%',
  zIndex: 1,
});

const CardImage = styled(Card.Image)({
  pointerEvents: 'auto',
});

const TagWrapper = styled.div({
  width: '100%',
  display: 'grid',
  gridTemplateColumns: 'fit-content(100%) fit-content(100%)',
  gap: '8px',
});

const IconButtonContainer = styled.div({
  bottom: 0,
  pointerEvents: 'auto',
  position: 'absolute' as const,
  right: 0,
});

const iconButtonStyles = (theme: Theme) => ({
  borderColor: transparentize(0.9, theme.colors.grays.black),
  borderWidth: 1,
  height: '48px',
  width: '48px',
});

/** 200px is enough room for 3 columns on an iPad mini */
export const productCardMinWidth = 200;
export const productCardMaxWidth = 226;

export const getColumnCount = () =>
  Math.floor(window.innerWidth / productCardMaxWidth);

export interface ProductCardProps {
  className?: string;
  disableQuickAdd?: boolean;
  disableWeightFilter?: boolean;
  displaySponsoredLabel?: boolean;
  highlightDeal?: boolean;
  inventoryCount?: number;
  isLoading?: boolean;
  isTopper?: boolean;
  /** A function that returns the path to navigate to, when this card is clicked. */
  navigateTo?: (productId: number, priceId?: PriceId) => string;
  onAddToBag?: ({ priceId, product }: AddToBagParams) => void;
  product: SmartSortProduct<AlgoliaProduct>;
  searchState?: JaneSearchState<any>;
  specialInScope?: StoreSpecial | V3StoreSpecial;
  visiblePriceIds?: PriceId[];
}

const InViewFlex = styled(InView)({
  display: 'flex',
  height: '100%',
  width: '100%',
});

export const ProductCard = ({
  className,
  disableQuickAdd,
  disableWeightFilter = false,
  highlightDeal,
  inventoryCount,
  isLoading = false,
  navigateTo,
  onAddToBag,
  product,
  searchState,
  specialInScope,
  isTopper = false,
  displaySponsoredLabel = true,
  visiblePriceIds,
}: ProductCardProps) => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const navigate = useNavigate();
  const productDetailsPath = useProductDetailsPath({
    isInlineAd: product.isSponsored && !isTopper,
  });
  const [isAdding, setIsAdding] = useState(false);

  const c1BundleTag = useFlag(FLAGS.c1BundleTag);
  const productAttributes = product.attributes;

  const { images: productImages } = useMemo(
    () => getProductImages(productAttributes),
    [productAttributes]
  );

  const { review_count: reviewCount, aggregate_rating: aggregateRating } =
    productAttributes;
  const { getKioskSpacing, isIpad } = useKioskSpacing();
  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const userSegments = useKioskUserSegmentIds();

  const { brandSpecials } = useBrandSpecials();
  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { data: v1Specials = [] } = useGetStoreSpecials(storeId, {
    enabled: !useV3 && !!storeId,
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);
  const searchingByWeight = disableWeightFilter
    ? undefined
    : (visiblePriceIds?.map((priceId) =>
        priceId.split('_').join(' ')
      ) as AlgoliaPriceId[]) ||
      (get(searchState, 'filters.available_weights') as AlgoliaPriceId[]);

  const getProductPrice = useGetProductPrices(
    productAttributes,
    specials,
    highlightDeal,
    searchingByWeight
  );

  const { category, brandSpecialId } = useParams<{
    brandSpecialId: string;
    category: MenuCategory;
  }>();

  useEffect(() => {
    if (isAdding) {
      delay(() => setIsAdding(false), 2500);
    }
  }, [isAdding, setIsAdding]);

  const productKindLabel = useMemo(() => {
    return category === productAttributes.kind
      ? customSubcategoryLabel({
          store,
          subcategory: {
            label: productAttributes.root_subtype ?? '',
            value: `${productAttributes.kind}:${productAttributes.root_subtype}`,
          },
        })
      : customCategoryLabel({ kind: productAttributes.kind, store });
  }, [category, productAttributes.kind, productAttributes.root_subtype, store]);

  const availableWeights = useMemo(() => {
    const weights: Weight[] = [];
    if (productAttributes.type && isPriceType(productAttributes.type)) {
      const allWeights = getMenuPrices(productAttributes.type);
      weights.push(
        ...allWeights.filter((weight) =>
          productAttributes.available_weights?.includes(
            weight.id.replace('_', ' ') as AlgoliaPriceId
          )
        )
      );
    }

    return visiblePriceIds
      ? weights.filter(({ id }) => visiblePriceIds.includes(id))
      : weights;
  }, [
    productAttributes.available_weights,
    productAttributes.type,
    visiblePriceIds,
  ]);

  const formattedProductWeights =
    availableWeights.length > 1
      ? `(${availableWeights[0].label} - ${
          availableWeights[availableWeights.length - 1].label
        })`
      : '';

  const { unitPrice, discountPrice, price, priceId } = getProductPrice();

  const discountPercentage = useMemo(() => {
    return calculateDiscountPercentage(unitPrice, discountPrice);
  }, [unitPrice, discountPrice]);
  const hasApplicableSpecial = c1BundleTag
    ? specials.some((special) =>
        doesSpecialApply(
          getSpecialIdsForTagLabel(productAttributes),
          special.id,
          special.special_type
        )
      )
    : false;

  const specialTagLabel = getSpecialTagLabel({
    product: productAttributes,
    store,
  });

  const shouldShowGold = useShouldKioskShowGold({ product: productAttributes });
  const janeGoldTagLabel =
    !brandSpecialId && shouldShowGold
      ? getApplicableGoldTagLabel({
          brandSpecials,
          applicableBrandSpecialIds:
            productAttributes?.applicable_brand_special_ids,
        })
      : false;

  const formattedPrice = buildPriceString({
    amount: productAttributes.amount,
    availableWeights,
    price,
    priceId,
    weightFilters: searchingByWeight,
  });

  const showReviewCount =
    store.product_reviews_enabled && (reviewCount || 0) >= 5;
  const potencyDesc = potencyDescription(productAttributes, priceId);

  const { pathname } = useLocation();
  const matches = useMatches();

  const onCardClick = useCallback(() => {
    product.click();

    const source = getSource({
      isTopper: true,
      matches,
      pathname,
    });

    trackProductCardTap({
      dealName: specialInScope ? [specialInScope.title] : null,
      dmType:
        product.isSponsored && !isTopper
          ? ['in-line']
          : isTopper
            ? ['bag']
            : null,
      potencyDisplayed: !!potencyDesc,
      priceDisplayed: formattedPrice,
      product: productAttributes,
      productPercentageOff: productAttributes.special_amount
        ? discountPercentage
        : null,
      productSavingsAmount:
        productAttributes.special_amount && unitPrice && discountPrice
          ? calculateDiscount(unitPrice, discountPrice)
          : null,
      ratingDisplayed: showReviewCount,
      source,
    });

    // NOTE: invalidate cache to ensure fresh load of product for product detail
    queryClient.invalidateQueries({
      queryKey: ['product', store.id, productAttributes.product_id],
    });

    const calcDestination = navigateTo ?? productDetailsPath;
    return navigate(
      calcDestination(
        productAttributes.product_id,
        getPriceIdForPercentThc(productAttributes, availableWeights)
      )
    );
  }, [
    availableWeights,
    discountPercentage,
    discountPrice,
    formattedPrice,
    matches,
    navigate,
    navigateTo,
    pathname,
    potencyDesc,
    product,
    productDetailsPath,
    showReviewCount,
    unitPrice,
    product,
    isTopper,
    productAttributes,
    userSegments,
  ]);

  const icon = useMemo(() => {
    if (isAdding) {
      return <CheckIcon color="grays-black" size={'md'} />;
    }
    return (
      <AddIcon
        color={disableQuickAdd ? 'grays-light' : 'grays-black'}
        size={'md'}
      />
    );
  }, [disableQuickAdd, isAdding]);

  const onAddToBagClick = useCallback(() => {
    if (isLoading || isAdding) {
      // Don't add more products to bag if the backend is loading or we're in the 2 second window.
      return;
    }

    onAddToBag?.({
      discountAmount:
        unitPrice && discountPrice
          ? calculateDiscount(unitPrice, discountPrice)
          : null,
      discountPercentage:
        unitPrice && discountPrice ? discountPercentage : null,
      priceId,
      product: productAttributes,
      productInstance: product,
    });

    if (availableWeights.length <= 1) {
      setIsAdding(true);
    }
  }, [
    availableWeights.length,
    discountPercentage,
    discountPrice,
    isAdding,
    isLoading,
    onAddToBag,
    priceId,
    product,
    unitPrice,
    product,
    productAttributes,
  ]);

  const showSponsoredLabel =
    product.isSponsored && !isTopper && displaySponsoredLabel;
  const productStatusLabel = getProductStatusLabel(
    showSponsoredLabel,
    inventoryCount
  );

  return (
    <InViewFlex
      className={className}
      data-testid={`product-card-container-${product.objectId}`}
      key={product.objectId}
      onChange={(inView: boolean) => {
        if (inView) {
          product.impress();
        }
      }}
      triggerOnce
      threshold={0.5}
      delay={1000}
    >
      <Card border="grays-ultralight" flat>
        <Box
          position="absolute"
          width="100%"
          zIndex={1}
          overflow="hidden"
          p={16}
          style={{ pointerEvents: 'none' }}
        >
          <ConditionalWrapper
            condition={
              !!janeGoldTagLabel && (!!discountPrice || hasApplicableSpecial)
            }
            wrapper={(children) => (
              <TagWrapper data-testid="deal-tags">{children}</TagWrapper>
            )}
          >
            {janeGoldTagLabel && <DealTag tagLabel={janeGoldTagLabel} isGold />}
            {(c1BundleTag ? hasApplicableSpecial : !!discountPrice) && (
              <DealTag tagLabel={specialTagLabel} />
            )}
          </ConditionalWrapper>
        </Box>
        <CardHeaderBox>
          <IconButtonContainer>
            <IconButton
              ariaLabel={`add ${productAttributes.name} to bag`}
              disabled={disableQuickAdd}
              disabledOpacity={1}
              onClick={onAddToBagClick}
              spacing={{ margin: getKioskSpacing(16, 16), padding: 12 }}
              style={iconButtonStyles(theme)}
              icon={icon}
            />
          </IconButtonContainer>
        </CardHeaderBox>
        <CardImage
          alt={productImages[0].imageAlt}
          ariaLabel={`view ${productAttributes.name} details`}
          onClick={onCardClick}
          aspectRatio="10/9"
          src={productImages[0].imageSrc}
          sizes="card"
          responsive
        />
        <Card.Content
          ariaLabel={`view ${productAttributes.name} details`}
          onClick={onCardClick}
        >
          <Flex
            flexDirection="column"
            height="100%"
            grow={1}
            px={getKioskSpacing(8, 0)}
          >
            <Flex
              justifyContent={
                productAttributes.category ? 'space-between' : 'flex-end'
              }
              pt={getKioskSpacing(8, 0)}
              mb={getKioskSpacing(12, 8)}
            >
              {productAttributes.category && (
                <Typography
                  color={getCategoryTagColor(productAttributes.category)}
                  variant="mini-bold"
                  style={{
                    flexShrink: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {customLineageLabel({
                    lineage: productAttributes.category,
                    store,
                  })}
                </Typography>
              )}
              {showReviewCount && (
                <StarRating
                  aggregateRating={aggregateRating || 0}
                  reviewCount={reviewCount || 0}
                  isCondensed
                />
              )}
            </Flex>

            {productStatusLabel && (
              <Typography
                color="grays-mid"
                mb={getKioskSpacing(2, 0)}
                variant={isIpad ? 'body' : 'mini'}
              >
                {productStatusLabel}
              </Typography>
            )}
            <Typography
              maxLines={product.isSponsored ? 1 : 2}
              mb={getKioskSpacing(2, 0)}
              variant="body-bold"
            >
              {productAttributes.name}
            </Typography>
            <Typography
              color="grays-mid"
              maxLines={1}
              mb={getKioskSpacing(2, 0)}
              variant={isIpad ? 'body' : 'mini'}
            >
              {productAttributes.brand}
            </Typography>
            <Typography
              color="grays-mid"
              mb={getKioskSpacing(2, 0)}
              variant={isIpad ? 'body' : 'mini'}
            >
              {productAttributes.brand_subtype || productKindLabel}{' '}
              {formattedProductWeights}
            </Typography>
            {potencyDesc && (
              <Typography
                color="grays-mid"
                maxLines={1}
                mb={getKioskSpacing(2, 0)}
                variant={isIpad ? 'body' : 'mini'}
              >
                {potencyDesc}
              </Typography>
            )}
            <Flex
              flexDirection="row"
              flexWrap="wrap"
              pt={getKioskSpacing(32, 16)}
              mt="auto"
              width="100%"
            >
              <ProductCardPrice
                unitPrice={unitPrice}
                discountPrice={discountPrice}
                formattedPrice={formattedPrice}
                hideStrikethroughPrice={hideStrikethroughPrice(
                  store?.state || ''
                )}
              />
            </Flex>
          </Flex>
        </Card.Content>
      </Card>
    </InViewFlex>
  );
};
